import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

import PipelineNodes from "@/components/PipelineNodes";
import AgentCard from "@/components/AgentCard";
import FinalPromptCard from "@/components/FinalPromptCard";
import HistorySidebar from "@/components/HistorySidebar";
import BriefComposer from "@/components/BriefComposer";
import { FORGE } from "@/constants/testIds";

export default function ForgePage() {
    const [config, setConfig] = useState(null);
    const [brief, setBrief] = useState("");
    const [llmKey, setLlmKey] = useState("claude-sonnet-4-5");
    const [brandIdx, setBrandIdx] = useState(0);
    const [contentIdx, setContentIdx] = useState(0);
    const [aspectIdx, setAspectIdx] = useState(0);

    const [running, setRunning] = useState(false);
    const [statuses, setStatuses] = useState({});
    const [outputs, setOutputs] = useState({});
    const [activeIndex, setActiveIndex] = useState(-1);
    const [error, setError] = useState(null);
    const [files, setFiles] = useState([]);
    const [wasCached, setWasCached] = useState(false);

    const [history, setHistory] = useState([]);
    const [activeRunId, setActiveRunId] = useState(null);
    const [taskContext, setTaskContext] = useState(null); // {task_id, ...} if coming from Tasks

    const pollRef = useRef(null);

    useEffect(() => {
        api.get("/config").then((res) => {
            setConfig(res.data);

            // Check for a task prefill in sessionStorage
            let prefill = null;
            try {
                const raw = sessionStorage.getItem("forge_prefill");
                if (raw) {
                    prefill = JSON.parse(raw);
                    sessionStorage.removeItem("forge_prefill");
                    setTaskContext(prefill);
                }
            } catch { /* ignore */ }

            setBrief(prefill?.brief || res.data.demo_brief || "");
            if (prefill?.brand) {
                const bi = res.data.brands.findIndex((b) => b.name === prefill.brand);
                if (bi >= 0) setBrandIdx(bi);
            }
            if (prefill?.content_type) {
                const ci = res.data.content_types.indexOf(prefill.content_type);
                if (ci >= 0) setContentIdx(ci);
            }
            if (prefill?.aspect_ratio) {
                const ai = res.data.aspect_ratios.indexOf(prefill.aspect_ratio);
                if (ai >= 0) setAspectIdx(ai);
            }

            if (res.data.llm_options?.length) {
                setLlmKey((prev) => (res.data.llm_options.find((o) => o.key === prev) ? prev : res.data.llm_options[0].key));
            }
            const s = {};
            res.data.agents.forEach((a) => (s[a.id] = "idle"));
            setStatuses(s);
        });
        loadHistory();
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, []);

    const loadHistory = async () => {
        try {
            const res = await api.get("/pipeline/history");
            setHistory(res.data);
        } catch (e) {
            console.error("history load", e);
        }
    };

    const llmLabel = useMemo(() => {
        if (!config) return "";
        const o = config.llm_options.find((x) => x.key === llmKey);
        return o ? o.label : llmKey;
    }, [config, llmKey]);

    const resetPipeline = () => {
        if (!config) return;
        const s = {};
        config.agents.forEach((a) => (s[a.id] = "idle"));
        setStatuses(s);
        setOutputs({});
        setActiveIndex(-1);
        setError(null);
        setWasCached(false);
    };

    const handleRun = useCallback(async () => {
        if (!config || !brief.trim() || running) return;
        resetPipeline();
        setRunning(true);
        setActiveRunId(null);

        try {
            const res = await api.post("/pipeline/run", {
                brief: brief.trim(),
                brand_index: brandIdx,
                content_type_index: contentIdx,
                aspect_index: aspectIdx,
                llm_key: llmKey,
                file_ids: files.map((f) => f.id),
            });
            const { run_id, cached } = res.data;
            setActiveRunId(run_id);
            setWasCached(Boolean(cached));
            startPolling(run_id);
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message || "Pipeline failed";
            setError(msg);
            setRunning(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brief, brandIdx, contentIdx, aspectIdx, llmKey, config, running, files]);

    const startPolling = (runId) => {
        if (pollRef.current) clearInterval(pollRef.current);
        const poll = async () => {
            try {
                const res = await api.get(`/pipeline/status/${runId}`);
                applyState(res.data);
                if (["completed", "failed", "cancelled"].includes(res.data.status)) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    setRunning(false);
                    setActiveIndex(-1);
                    if (res.data.cached) setWasCached(true);
                    if (res.data.status === "failed" && res.data.error) {
                        setError(res.data.error);
                    }
                    // If this run originated from a Task, link it back so assigner sees the update
                    if (taskContext?.task_id && res.data.status === "completed") {
                        try {
                            await api.post(`/tasks/${taskContext.task_id}/link-run`, {
                                run_id: runId,
                            });
                        } catch (e) {
                            console.warn("link task run failed", e);
                        }
                    }
                    loadHistory();
                }
            } catch (e) {
                console.error("poll", e);
            }
        };
        pollRef.current = setInterval(poll, 700);
        poll();
    };

    const applyState = (state) => {
        if (!state) return;
        setStatuses(state.agent_states || {});
        setOutputs(state.outputs || {});
        if (state.active_agent && config) {
            const idx = config.agents.findIndex((a) => a.id === state.active_agent);
            setActiveIndex(idx);
        } else {
            setActiveIndex(-1);
        }
    };

    const handleStop = async () => {
        if (activeRunId) {
            try {
                await api.post(`/pipeline/cancel/${activeRunId}`);
            } catch (e) {
                console.error(e);
            }
        }
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        setRunning(false);
        setActiveIndex(-1);
    };

    const handleLoadDemo = () => {
        if (config) setBrief(config.demo_brief || "");
    };

    const handleSelectHistory = async (id) => {
        try {
            const res = await api.get(`/pipeline/history/${id}`);
            const doc = res.data;
            setActiveRunId(id);
            setBrief(doc.brief);
            if (config) {
                const bi = config.brands.findIndex((b) => b.name === doc.brand);
                if (bi >= 0) setBrandIdx(bi);
                const ci = config.content_types.indexOf(doc.content_type);
                if (ci >= 0) setContentIdx(ci);
                const ai = config.aspect_ratios.indexOf(doc.aspect_ratio);
                if (ai >= 0) setAspectIdx(ai);
            }
            setOutputs(doc.outputs || {});
            const s = {};
            (config?.agents || []).forEach((a) => {
                s[a.id] = doc.outputs && doc.outputs[a.id] ? "done" : "idle";
            });
            setStatuses(s);
            setError(null);
            setWasCached(false);
        } catch (e) {
            console.error(e);
        }
    };

    const handleNewRun = () => {
        setActiveRunId(null);
        resetPipeline();
        if (config) setBrief(config.demo_brief || "");
    };

    const finalOutput = outputs.assembler || "";

    return (
        <>
            {/* Sticky pipeline visualization */}
            <div className="sticky top-[72px] z-30 bg-[color:var(--app-bg)]/90 backdrop-blur-xl border-b border-[color:var(--border-subtle)]">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-4 overflow-x-auto">
                    {config && (
                        <PipelineNodes
                            agents={config.agents}
                            statuses={statuses}
                            activeIndex={activeIndex}
                        />
                    )}
                </div>
            </div>

            <main className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8 lg:py-10" data-testid={FORGE.finalPromptCard + "-page"}>
                {taskContext?.task_id && (
                    <div className="mb-4 text-xs bg-white border border-[color:var(--profecia-gold-subtle)] rounded-md px-4 py-2.5 inline-flex items-center gap-3">
                        <span className="font-mono uppercase tracking-widest text-[color:var(--profecia-gold)]">
                            Working on task
                        </span>
                        <span className="text-[color:var(--text-main)]">
                            Completed run will auto-link back and notify your assigner.
                        </span>
                    </div>
                )}
                {wasCached && (
                    <div className="mb-4 text-xs text-[color:var(--profecia-gold)] bg-[color:var(--profecia-gold-light)] border border-[color:var(--profecia-gold-subtle)] rounded-md px-4 py-2 inline-flex items-center gap-2">
                        <span className="font-mono uppercase tracking-widest">Context cache hit</span>
                        <span>— reused a matching recent run · no additional tokens spent</span>
                    </div>
                )}
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
                    <div className="flex-1 min-w-0 space-y-8">
                        <BriefComposer
                            brief={brief}
                            setBrief={setBrief}
                            llmKey={llmKey}
                            setLlmKey={setLlmKey}
                            brandIdx={brandIdx}
                            setBrandIdx={setBrandIdx}
                            contentIdx={contentIdx}
                            setContentIdx={setContentIdx}
                            aspectIdx={aspectIdx}
                            setAspectIdx={setAspectIdx}
                            files={files}
                            setFiles={setFiles}
                            config={config}
                            running={running}
                            onRun={handleRun}
                            onStop={handleStop}
                            onLoadDemo={handleLoadDemo}
                            error={error}
                        />

                        {config && (
                            <section className="space-y-4">
                                <div className="flex items-baseline justify-between">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--profecia-gold)] font-mono">
                                            Agent Outputs
                                        </div>
                                        <h2 className="font-serif text-2xl md:text-3xl text-[color:var(--text-main)] leading-none mt-1">
                                            Pipeline Transcript
                                        </h2>
                                    </div>
                                    <div className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)]">
                                        Driving LLM · {llmLabel}
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    {config.agents.map((agent, idx) => (
                                        <AgentCard
                                            key={agent.id}
                                            agent={agent}
                                            index={idx}
                                            status={statuses[agent.id] || "idle"}
                                            output={outputs[agent.id] || ""}
                                            defaultOpen={idx === 0}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}

                        <FinalPromptCard finalOutput={finalOutput} running={running} />
                    </div>

                    <HistorySidebar
                        history={history}
                        activeRunId={activeRunId}
                        onSelect={handleSelectHistory}
                        onNewRun={handleNewRun}
                    />
                </div>
            </main>
        </>
    );
}
